import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/index';
import { selectUser, selectIsAuthenticated } from '@features/auth/authSlice';
import { Typography, Box } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';

interface PermissionRouteProps {
  requiredRoles?: string[];
  requiredPermissions?: string[];
  requireAdmin?: boolean;
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  requiredRoles = [],
  requiredPermissions = [],
  requireAdmin = false,
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const location = useLocation();

  // Check authentication first
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get all user roles (check both 'role' and 'roles' for compatibility)
  const userRoles: string[] = [];
  if (user.role) {
    userRoles.push(user.role);
  }
  if (user.roles && Array.isArray(user.roles)) {
    user.roles.forEach(r => {
      if (!userRoles.includes(r)) {
        userRoles.push(r);
      }
    });
  }

  // Check admin requirement
  if (requireAdmin && !userRoles.includes('admin')) {
    return <AccessDenied reason="This page requires administrator privileges." />;
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      return <AccessDenied reason="You don't have the required role to access this page." />;
    }
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const userPermissions = user.permissions || [];
    const hasAllPermissions = requiredPermissions.every(permission =>
      userPermissions.includes(permission)
    );

    if (!hasAllPermissions) {
      return <AccessDenied reason="You don't have sufficient permissions to access this page." />;
    }
  }

  // All checks passed, render the child routes
  return <Outlet />;
};

// Access Denied Component
const AccessDenied: React.FC<{ reason: string }> = ({ reason }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <LockOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h4" gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
        {reason}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
        If you believe you should have access to this page, please contact your administrator.
      </Typography>
    </Box>
  );
};

export default PermissionRoute;
